import { supabase } from "@/lib/supabaseClient"; // ✅ fallback to Supabase
const API_BASE_URL = process.env.REACT_APP_API_URL;

// 🟢 Helper: Get auth header
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==========================================================
// 🧠 Fallback Helper — use Supabase if backend fails
// ==========================================================
const safeFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    //console.warn(`⚠️ Backend unreachable, switching to Supabase: ${url}`);
    return null;
  }
};



// ==========================================================
// 💖 Wishlist Integration (Backend → Supabase fallback)
// ==========================================================
export const fetchWishlist = async () => {
  try {
    const res = await safeFetch(`${API_BASE_URL}/products/wishlist`, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
    });

    if (!res) {
      const { data, error } = await supabase.from("wishlist").select("*");
      if (error) throw new Error(error.message);
      return data || [];
    }

    return res;
  } catch (error) {
    console.error("❌ Error fetching wishlist:", error.message);
    return [];
  }
};

export const addToWishlist = async (productId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/products/wishlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ productId }),
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to add to wishlist");
    return await res.json();
  } catch (error) {
    //console.warn("⚠️ Backend addToWishlist failed — trying Supabase...");
    const { data, error: supaErr } = await supabase
      .from("wishlist")
      .insert([{ product_id: productId }])
      .select()
      .single();

    if (supaErr) console.error("❌ Supabase wishlist error:", supaErr.message);
    return data || null;
  }
};

export const removeFromWishlist = async (productId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/products/wishlist/${productId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to remove from wishlist");
    return await res.json();
  } catch (error) {
    //console.warn("⚠️ Backend removeFromWishlist failed — trying Supabase...");
    const { error: supaErr } = await supabase
      .from("wishlist")
      .delete()
      .eq("product_id", productId);
    if (supaErr) console.error("❌ Supabase wishlist delete error:", supaErr.message);
    return { success: !supaErr };
  }
};

export const checkIfWishlisted = async (productId, userId) => {
  try {
    const res = await safeFetch(
      `${API_BASE_URL}/products/wishlist/check?product_id=${productId}&user_id=${userId}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
        credentials: "include",
      }
    );

    if (!res) {
      const { data, error } = await supabase
        .from("wishlist")
        .select("*")
        .eq("product_id", productId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return !!data;
    }

    return res.isWishlisted || false;
  } catch (error) {
    console.error("❌ Error checking wishlist:", error.message);
    return false;
  }
};
